import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { useOwnUndoState } from "../useOwnUndoState";
import { describeOwnUndoTarget } from "../../utils/describeOwnUndoTarget";
import type { HistoryEntry, HistoryEventDirection } from "../../types";
import type {
  OwnUndoAction,
  UseOwnUndoRedoArgs,
  UseOwnUndoRedoResult,
} from "./types";

export const useOwnUndoRedo = ({
  projectId,
  subject,
  currentUserEmail,
  revertingEntryId,
  revert,
  isPaused,
}: UseOwnUndoRedoArgs): UseOwnUndoRedoResult => {
  const { t, i18n } = useTranslation();
  const ownUndo = useOwnUndoState({ projectId, subject });

  // A refresh after the last change may still name the entry that change replaced.
  const isBusy = revertingEntryId !== null || ownUndo.isFetching;

  const runFor = useCallback(
    (
      target: HistoryEntry | null,
      direction: HistoryEventDirection,
      label: string,
      isRevertible: boolean,
    ) => {
      if (isBusy || isPaused) return;
      if (target === null || target.action === null) {
        toast.info(label);
        return;
      }
      if (!isRevertible) {
        toast.error(label);
        return;
      }
      revert({ entryId: target.id, direction, planCount: target.planCount });
    },
    [isBusy, isPaused, revert],
  );

  const actionFor = useCallback(
    (
      target: HistoryEntry | null,
      direction: HistoryEventDirection,
    ): OwnUndoAction => {
      const { label, isRevertible } = describeOwnUndoTarget({
        target,
        direction,
        currentUserEmail,
        locale: i18n.language,
        t,
      });
      return {
        label,
        isAvailable: isRevertible && !isBusy && !isPaused,
        run: () => runFor(target, direction, label, isRevertible),
      };
    },
    [currentUserEmail, i18n.language, t, isBusy, isPaused, runFor],
  );

  const actions = useMemo(
    (): UseOwnUndoRedoResult => ({
      undo: actionFor(ownUndo.undo, "undo"),
      redo: actionFor(ownUndo.redo, "redo"),
    }),
    [actionFor, ownUndo.undo, ownUndo.redo],
  );

  return actions;
};
